import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import { FolderCopy as FolderCopyIcon, PlayArrow as RunIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const TYPE_LABELS = {
  resume: 'Resume',
  cover_letter: 'Cover-Letter',
  client_agreement: 'Client-Agreement',
  vendor_agreement: 'Vendor-Agreement',
  nda: 'NDA',
  sow: 'State-Of-Work',
  po: 'Purchase-Order',
  invoice: 'Invoice',
  timesheet: 'Timesheet',
  id_proof: 'ID-Proof',
  work_authorization: 'Work-Authorization',
  other: 'Other',
};

export default function DocumentBackupAdmin() {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/document-backup/status');
      setStatus(r.data?.data ?? null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load document backup status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runBackup = async () => {
    setRunning(true);
    try {
      const r = await api.post('/admin/document-backup/run', {});
      toast.success(r.data?.message || 'Document backup finished');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Backup run failed');
    } finally {
      setRunning(false);
    }
  };

  const counts = status?.fileCountsByTypeFolder || {};
  const last = status?.lastSync;

  return (
    <Box sx={{ p: 2, maxWidth: 960, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <FolderCopyIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Document Backup Administration
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} size="small">
          Refresh
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
          onClick={runBackup}
          disabled={running || loading}
        >
          Run backup now
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        New uploads are stored under <code>ERPForC/documents/&lt;type&gt;</code> (persistent volume). When you run a backup,
        files are copied from that live store into <code>ERPForC/backup/documents/&lt;type&gt;</code> (backup mirror volume).
      </Alert>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && status && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Paths (server)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              Live documents (PVC): {status.documentsRoot}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}>
              Backup mirror: {status.documentBackupRoot}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}>
              Legacy uploads (non-document modules): {status.legacyUploadDir}
            </Typography>
            {last?.lastRunAt && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Last run: {new Date(last.lastRunAt).toLocaleString()} — copied {last.copied ?? '—'} /{' '}
                {last.totalDocuments ?? '—'} documents (skipped {last.skipped ?? '—'}, errors {last.errors ?? '—'})
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {!loading && status && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Files per type folder
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Folder</TableCell>
                  <TableCell align="right">Backed-up files</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(TYPE_LABELS).map((key) => (
                  <TableRow key={key}>
                    <TableCell>{key}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{TYPE_LABELS[key]}</TableCell>
                    <TableCell align="right">{counts[key] ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
